import { notification } from "antd";
import { get } from "lodash";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { StoreContext } from "shared/store";
import graphql from "utils/api/graphql";
import { EXPORT_SUBSCRIPTIONS } from "utils/api/graphql/mutations/subscription";
import { CONTENT_TYPES, SUBSCRIPTION_STATUSES } from "utils/constants";
import { downloadDocument } from "utils/helpers/files";
import ExportModal from "../../Projects/List/ExportModal";
import View from "./View";

const List = () => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const { user } = useContext(StoreContext);
  const [pagination, setPagination] = useState({});
  const [filter, setFilter] = useState({
    where: {
      status: SUBSCRIPTION_STATUSES.SIGNED,
      project: {
        visible: true,
      },
    },
  });

  const [exportSubscriptions, { loading: exportLoading }] =
    useMutation(EXPORT_SUBSCRIPTIONS);
  const [sendSubscriptionsByEmail, { loading: sendByEmailLoading }] =
    useMutation(graphql.mutations.SEND_SUBSCRIPTIONS_BY_EMAIL);

  const [exportCollectiveSubscriptions, { loading: exportCollectiveLoading }] =
    useMutation(EXPORT_SUBSCRIPTIONS);
  const [
    sendCollectiveSubscriptionsByEmail,
    { loading: sendCollectiveByEmailLoading },
  ] = useMutation(graphql.mutations.SEND_SUBSCRIPTIONS_BY_EMAIL);

  const [showExportModal, setShowExportModal] = useState(false);
  const [showExportCollectiveModal, setShowExportCollectiveModal] =
    useState(false);

  const handleExportSubscriptions = async (variables = { where: {} }) => {
    try {
      const exportVariables = {
        where: {
          status: SUBSCRIPTION_STATUSES.SIGNED,
          project: {
            visible: true,
          },
          ...variables.where,
        },
      };

      if (variables.gt) {
        exportVariables.gt = variables.gt;
      }
      if (variables.lt) {
        exportVariables.lt = variables.lt;
      }

      const { data } = await exportSubscriptions({
        variables: exportVariables,
      });
      if (data?.exportSubscriptions) {
        downloadDocument({
          base64: data.exportSubscriptions,
          contentType: CONTENT_TYPES.XLSX,
          name: "subscriptions.xlsx",
        });
      }
    } catch (error) {
      console.error("Error exporting subscriptions:", error);
    }
  };

  const handleSendSubscriptionsByEmail = async (variables = { where: {} }) => {
    try {
      const exportVariables = {
        where: {
          status: SUBSCRIPTION_STATUSES.SIGNED,
          project: {
            visible: true,
          },
          ...variables.where,
        },
      };

      if (variables.gt) {
        exportVariables.gt = variables.gt;
      }
      if (variables.lt) {
        exportVariables.lt = variables.lt;
      }

      // Afficher une notification indiquant que l'email sera envoyé
      notification.info({
        message: t("LIST.HEADER.EXPORT_MODAL.EMAIL_WILL_BE_SENT_TITLE"),
        description: t("LIST.HEADER.EXPORT_MODAL.EMAIL_WILL_BE_SENT_MESSAGE", {
          email: user?.email || "",
        }),
        duration: 10,
      });

      const { data } = await sendSubscriptionsByEmail({
        variables: exportVariables,
      });
      if (data?.sendSubscriptionsByEmail !== undefined) {
        // Success - email sent
        notification.success({
          message: t("LIST.HEADER.EXPORT_MODAL.EMAIL_SENT_SUCCESS_TITLE"),
          description: t(
            "LIST.HEADER.EXPORT_MODAL.EMAIL_SENT_SUCCESS_MESSAGE",
            {
              count: data.sendSubscriptionsByEmail,
            },
          ),
          duration: 10,
        });
      }
    } catch (error) {
      console.error("Error sending subscriptions by email:", error);
      notification.error({
        message: t("LIST.HEADER.EXPORT_MODAL.EMAIL_SENT_ERROR_TITLE"),
        description: error.message,
        duration: 10,
      });
    }
  };

  const handleExportCollectiveSubscriptions = async (
    variables = { where: {} },
    organizationName = null,
  ) => {
    try {
      const exportVariables = {
        where: {
          status: SUBSCRIPTION_STATUSES.SIGNED,
          project: {
            visible: false,
          },
          ...variables.where,
        },
      };

      if (variables.gt) {
        exportVariables.gt = variables.gt;
      }
      if (variables.lt) {
        exportVariables.lt = variables.lt;
      }

      const { data } = await exportCollectiveSubscriptions({
        variables: exportVariables,
      });
      if (data?.exportSubscriptions) {
        const fileName = organizationName
          ? `contrats_${organizationName.replace(/[^a-zA-Z0-9]/g, "_")}.xlsx`
          : "contrats.xlsx";
        downloadDocument({
          base64: data.exportSubscriptions,
          contentType: CONTENT_TYPES.XLSX,
          name: fileName,
        });
      }
    } catch (error) {
      console.error("Error exporting collective subscriptions:", error);
    }
  };

  const handleSendCollectiveSubscriptionsByEmail = async (
    variables = { where: {} },
  ) => {
    try {
      const exportVariables = {
        where: {
          status: SUBSCRIPTION_STATUSES.SIGNED,
          project: {
            visible: false,
          },
          ...variables.where,
        },
      };

      if (variables.gt) {
        exportVariables.gt = variables.gt;
      }
      if (variables.lt) {
        exportVariables.lt = variables.lt;
      }

      // Afficher une notification indiquant que l'email sera envoyé
      notification.info({
        message: t("LIST.HEADER.EXPORT_MODAL.EMAIL_WILL_BE_SENT_TITLE"),
        description: t("LIST.HEADER.EXPORT_MODAL.EMAIL_WILL_BE_SENT_MESSAGE", {
          email: user?.email || "",
        }),
        duration: 10,
      });

      const { data } = await sendCollectiveSubscriptionsByEmail({
        variables: exportVariables,
      });
      if (data?.sendSubscriptionsByEmail !== undefined) {
        // Success - email sent
        notification.success({
          message: t("LIST.HEADER.EXPORT_MODAL.EMAIL_SENT_SUCCESS_TITLE"),
          description: t(
            "LIST.HEADER.EXPORT_MODAL.EMAIL_SENT_SUCCESS_MESSAGE",
            {
              count: data.sendSubscriptionsByEmail,
            },
          ),
          duration: 10,
        });
      }
    } catch (error) {
      console.error("Error sending collective subscriptions by email:", error);
      notification.error({
        message: t("LIST.HEADER.EXPORT_MODAL.EMAIL_SENT_ERROR_TITLE"),
        description: error.message,
        duration: 10,
      });
    }
  };

  const onSearch = (target) => {
    setPagination((prev) => ({ ...prev, skip: 0 }));
    setFilter((prev) => ({
      ...prev,
      like: {
        ...prev.like,
        OR: [
          {
            project: {
              contact: {
                user: {
                  firstname: `%${target.value}%`,
                },
              },
            },
          },
          {
            project: {
              contact: {
                user: {
                  lastname: `%${target.value}%`,
                },
              },
            },
          },
        ],
      },
    }));
  };

  return (
    <>
      <View
        filter={filter}
        pagination={pagination}
        setPagination={setPagination}
        onSearch={onSearch}
        exportLoading={exportLoading}
        exportSubscriptions={() => setShowExportModal(true)}
        exportCollectiveLoading={exportCollectiveLoading}
        exportCollectiveSubscriptions={() => setShowExportCollectiveModal(true)}
      />
      <ExportModal
        visible={showExportModal}
        onCancel={() => setShowExportModal(false)}
        onConfirm={handleExportSubscriptions}
        onSendByEmail={handleSendSubscriptionsByEmail}
        exportLoading={exportLoading}
        sendByEmailLoading={sendByEmailLoading}
        baseWhere={{
          status: SUBSCRIPTION_STATUSES.SIGNED,
          project: {
            visible: true,
          },
        }}
      />
      <ExportModal
        visible={showExportCollectiveModal}
        onCancel={() => setShowExportCollectiveModal(false)}
        onConfirm={handleExportCollectiveSubscriptions}
        onSendByEmail={handleSendCollectiveSubscriptionsByEmail}
        exportLoading={exportCollectiveLoading}
        sendByEmailLoading={sendCollectiveByEmailLoading}
        baseWhere={{
          status: SUBSCRIPTION_STATUSES.SIGNED,
          project: {
            visible: false,
          },
        }}
        requiredOrganization={true}
        organizationFilter={{
          contracts: {
            organization: {
              id: get(user, "organization.id"),
            },
          },
        }}
      />
    </>
  );
};

export default List;
